import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  Alert,
  Collapse,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  VpnKey as VpnKeyIcon,
  PowerSettingsNew as PowerIcon,
  SwapHoriz as SwapIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getAllStores,
  createStore,
  updateStore,
  deleteStore,
  activateStore,
  deactivateStore,
  assignUserToStore,
  removeUserFromStore,
  moveUserToStore,
  searchStores,
  getStoreUsers,
} from '../services/storeUserService';
import {
  getAllUsers,
  getUserById,
  searchUsers,
  createUser,
  updateUser,
  deleteUser,
  enableUser,
  disableUser,
  updateUserPassword,
  getAvailableRoles,
  changeUserRole,
  removeRoleFromUser,
} from '../services/userManagementService';

const AddStoreAndUser = () => {
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Dialog states
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [moveUserDialogOpen, setMoveUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  
  // Menu states
  const [storeMenuAnchor, setStoreMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [menuStore, setMenuStore] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  
  // Form states
  const [storeForm, setStoreForm] = useState({ 
    storeName: '',
    storeCode: '',
    description: '',
    street: '',
    streetNumber: '',
    postCode: '',
    city: '',
    country: '',
    email: '',
    phoneNumber: '',
    ibaNumber: '',
    bicNumber: '',
    isActive: true
  });
  const [userForm, setUserForm] = useState({ 
    userName: '',
    fullName: '', 
    email: '', 
    password: '', 
    confirmPassword: '',
    phoneNumber: '',
    roleName: 'User',
    storeId: null,
    notes: '',
    isActive: true 
  });
  const [passwordForm, setPasswordForm] = useState({ password: '', confirmPassword: '' });
  const [targetStoreId, setTargetStoreId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');

  // Load data on mount
  useEffect(() => {
    loadData();
    loadRoles();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const storesResponse = await getAllStores();
      const storesData = storesResponse.payload || storesResponse || [];
      setStores(storesData);
      
      // Try to fetch users, but don't fail if it doesn't work
      try {
        const usersResponse = await getAllUsers();
        
        // ✅ Check if response is an error (authentication failure)
        if (usersResponse?.status === 'Failure' || usersResponse?.message === 'User not authenticated') {
          console.warn('⚠️ User not authenticated for getAllUsers');
          toast.warning('User authentication required for user management');
          setUsers([]);
          return;
        }
        
        // ✅ Extract users array from response
        const usersData = usersResponse?.payload || usersResponse || [];
        
        // ✅ Ensure usersData is an array
        if (Array.isArray(usersData)) {
          setUsers(usersData);
        } else {
          console.warn('⚠️ Users response is not an array:', usersData);
          setUsers([]);
        }
      } catch (userError) {
        console.error('❌ Error fetching users:', userError);
        toast.info('User management features will be available soon');
        setUsers([]);
      }
      
    } catch (error) {
      console.error('Error loading stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const rolesResponse = await getAvailableRoles();
      // Extract roles array from response
      const rolesArray = rolesResponse.roles || [];
      // Map to get displayName or name
      const roleNames = rolesArray.map(role => role.displayName || role.name);
      setRoles(roleNames);
    } catch (error) {
      console.error('Error loading roles:', error);
      // Set default roles if API fails
      setRoles(['User', 'Admin', 'Manager']);
    }
  };

  // Group users by store
  const groupedUsers = React.useMemo(() => {
    // ✅ Ensure users is an array before filtering
    const usersArray = Array.isArray(users) ? users : [];
    
    const grouped = {
      unassigned: usersArray.filter(user => !user.StoreId),
    };
    
    stores.forEach(store => {
      grouped[store.Id] = usersArray.filter(user => user.StoreId === store.Id);
    });
    
    return grouped;
  }, [users, stores]);

  // Filter users and stores based on search
  const filteredStores = stores.filter(store =>
    store.StoreName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.City?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.StoreCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUnassignedUsers = (groupedUsers.unassigned || []).filter(user =>
    user.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.Email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Store handlers
  const handleCreateStore = async () => {
    try {
      // Validate required fields
      if (!storeForm.storeName.trim()) {
        toast.error('Store name is required');
        return;
      }
      if (storeForm.storeName.length > 100) {
        toast.error('Store name cannot exceed 100 characters');
        return;
      }
      
      if (!storeForm.storeCode.trim()) {
        toast.error('Store code is required');
        return;
      }
      if (storeForm.storeCode.length < 2 || storeForm.storeCode.length > 10) {
        toast.error('Store code must be between 2 and 10 characters');
        return;
      }
      if (!/^[A-Z0-9]+$/.test(storeForm.storeCode)) {
        toast.error('Store code must contain only uppercase letters and numbers');
        return;
      }
      
      if (!storeForm.street.trim()) {
        toast.error('Street is required');
        return;
      }
      if (!storeForm.postCode.trim()) {
        toast.error('Post code is required');
        return;
      }
      if (!storeForm.city.trim()) {
        toast.error('City is required');
        return;
      }
      if (!storeForm.country.trim()) {
        toast.error('Country is required');
        return;
      }
      
      if (!storeForm.email.trim()) {
        toast.error('Email is required');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(storeForm.email)) {
        toast.error('Invalid email format');
        return;
      }
      
      if (!storeForm.phoneNumber.trim()) {
        toast.error('Phone number is required');
        return;
      }
      
      await createStore(storeForm);
      toast.success('Store created successfully');
      setStoreDialogOpen(false);
      setStoreForm({ 
        storeName: '',
        storeCode: '',
        description: '',
        street: '',
        streetNumber: '',
        postCode: '',
        city: '',
        country: '',
        email: '',
        phoneNumber: '',
        ibaNumber: '',
        bicNumber: '',
        isActive: true
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create store');
    }
  };

  const handleUpdateStore = async () => {
    try {
      // Validate required fields
      if (!storeForm.storeName.trim()) {
        toast.error('Store name is required');
        return;
      }
      if (storeForm.storeName.length > 100) {
        toast.error('Store name cannot exceed 100 characters');
        return;
      }
      
      if (!storeForm.storeCode.trim()) {
        toast.error('Store code is required');
        return;
      }
      if (storeForm.storeCode.length < 2 || storeForm.storeCode.length > 10) {
        toast.error('Store code must be between 2 and 10 characters');
        return;
      }
      if (!/^[A-Z0-9]+$/.test(storeForm.storeCode)) {
        toast.error('Store code must contain only uppercase letters and numbers');
        return;
      }
      
      if (!storeForm.street.trim()) {
        toast.error('Street is required');
        return;
      }
      if (!storeForm.postCode.trim()) {
        toast.error('Post code is required');
        return;
      }
      if (!storeForm.city.trim()) {
        toast.error('City is required');
        return;
      }
      if (!storeForm.country.trim()) {
        toast.error('Country is required');
        return;
      }
      
      if (!storeForm.email.trim()) {
        toast.error('Email is required');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(storeForm.email)) {
        toast.error('Invalid email format');
        return;
      }
      
      if (!storeForm.phoneNumber.trim()) {
        toast.error('Phone number is required');
        return;
      }
      
      await updateStore(selectedStore.Id, storeForm);
      toast.success('Store updated successfully');
      setStoreDialogOpen(false);
      setSelectedStore(null);
      setStoreForm({ 
        storeName: '',
        storeCode: '',
        description: '',
        street: '',
        streetNumber: '',
        postCode: '',
        city: '',
        country: '',
        email: '',
        phoneNumber: '',
        ibaNumber: '',
        bicNumber: '',
        isActive: true
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update store');
    }
  };

  const handleDeleteStore = async () => {
    try {
      const storeUsers = groupedUsers[deleteTarget.Id] || [];
      if (storeUsers.length > 0) {
        toast.error('Cannot delete store with assigned users. Please reassign users first.');
        return;
      }
      
      await deleteStore(deleteTarget.Id);
      toast.success('Store deleted successfully');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete store');
    }
  };

  const handleToggleStoreStatus = async (store) => {
    try {
      if (store.IsActive) {
        await deactivateStore(store.Id);
        toast.success('Store deactivated successfully');
      } else {
        await activateStore(store.Id);
        toast.success('Store activated successfully');
      }
      loadData();
    } catch (error) {
      toast.error('Failed to toggle store status');
    }
  };

  // User handlers
  const handleCreateUser = async () => {
    try {
      // Validate required fields
      if (!userForm.userName.trim()) {
        toast.error('Username is required');
        return;
      }
      if (userForm.userName.length < 3 || userForm.userName.length > 50) {
        toast.error('Username must be between 3 and 50 characters');
        return;
      }
      
      if (!userForm.fullName.trim()) {
        toast.error('Full name is required');
        return;
      }
      if (userForm.fullName.length > 100) {
        toast.error('Name cannot exceed 100 characters');
        return;
      }
      
      if (!userForm.email.trim()) {
        toast.error('Email is required');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
        toast.error('Invalid email format');
        return;
      }
      if (userForm.email.length > 256) {
        toast.error('Email cannot exceed 256 characters');
        return;
      }
      
      if (!userForm.password.trim()) {
        toast.error('Password is required');
        return;
      }
      if (userForm.password.length < 6 || userForm.password.length > 100) {
        toast.error('Password must be between 6 and 100 characters');
        return;
      }
      
      if (!userForm.confirmPassword.trim()) {
        toast.error('Password confirmation is required');
        return;
      }
      if (userForm.password !== userForm.confirmPassword) {
        toast.error('Password and confirmation password do not match');
        return;
      }
      
      if (userForm.phoneNumber && userForm.phoneNumber.length > 20) {
        toast.error('Phone number cannot exceed 20 characters');
        return;
      }
      
      if (!userForm.roleName) {
        toast.error('Role is required');
        return;
      }
      
      if (userForm.notes && userForm.notes.length > 500) {
        toast.error('Notes cannot exceed 500 characters');
        return;
      }
      
      // Prepare payload
      const payload = {
        userName: userForm.userName,
        fullName: userForm.fullName,
        email: userForm.email,
        password: userForm.password,
        confirmPassword: userForm.confirmPassword,
        phoneNumber: userForm.phoneNumber || null,
        roleName: userForm.roleName,
        storeId: userForm.storeId || null,
        notes: userForm.notes || null,
        isActive: userForm.isActive
      };
      
      await createUser(payload);
      toast.success('User created successfully');
      setUserDialogOpen(false);
      setUserForm({ 
        userName: '',
        fullName: '', 
        email: '', 
        password: '', 
        confirmPassword: '',
        phoneNumber: '',
        roleName: 'User',
        storeId: null,
        notes: '',
        isActive: true 
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      if (!userForm.fullName.trim() || !userForm.email.trim()) {
        toast.error('Full name and email are required');
        return;
      }
      
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
        toast.error('Invalid email format');
        return;
      }
      
      const updateData = {
        fullName: userForm.fullName,
        email: userForm.email,
        phoneNumber: userForm.phoneNumber || null,
        roleName: userForm.roleName,
        storeId: userForm.storeId || null,
        notes: userForm.notes || null,
      };
      
      await updateUser(selectedUser.Id, updateData);
      toast.success('User updated successfully');
      setUserDialogOpen(false);
      setSelectedUser(null);
      setUserForm({ 
        userName: '',
        fullName: '', 
        email: '', 
        password: '', 
        confirmPassword: '',
        phoneNumber: '',
        roleName: 'User',
        storeId: null,
        notes: '',
        isActive: true 
      });
      loadData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(deleteTarget.Id);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      if (user.IsActive) {
        await disableUser(user.Id);
        toast.success('User deactivated successfully');
      } else {
        await enableUser(user.Id);
        toast.success('User activated successfully');
      }
      loadData();
    } catch (error) {
      toast.error('Failed to toggle user status');
    }
  };

  const handleUpdatePassword = async () => {
    try {
      if (!passwordForm.password.trim()) {
        toast.error('Password is required');
        return;
      }
      
      if (passwordForm.password !== passwordForm.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      
      if (passwordForm.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      
      await updateUserPassword(selectedUser.Id, passwordForm.password);
      toast.success('Password updated successfully');
      setPasswordDialogOpen(false);
      setPasswordForm({ password: '', confirmPassword: '' });
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const handleMoveUser = async () => {
    try {
      if (!targetStoreId) {
        toast.error('Please select a target store');
        return;
      }
      
      if (targetStoreId === 'unassign') {
        await removeUserFromStore(selectedUser.Id);
        toast.success('User unassigned successfully');
      } else {
        // If user already has a store, use move-user, otherwise use assign-user
        if (selectedUser.StoreId) {
          await moveUserToStore(selectedUser.Id, selectedUser.StoreId, targetStoreId);
          toast.success('User moved successfully');
        } else {
          await assignUserToStore(selectedUser.Id, targetStoreId);
          toast.success('User assigned successfully');
        }
      }
      
      setMoveUserDialogOpen(false);
      setSelectedUser(null);
      setTargetStoreId('');
      loadData();
    } catch (error) {
      toast.error('Failed to move user');
    }
  };

  const handleChangeRole = async () => {
    try {
      if (!selectedRole) {
        toast.error('Please select a role');
        return;
      }
      
      // Change user role
      await changeUserRole(selectedUser.Id, selectedRole);
      toast.success('Role changed successfully');
      setRoleDialogOpen(false);
      setSelectedUser(null);
      setSelectedRole('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change role');
    }
  };

  // Menu handlers
  const handleStoreMenuOpen = (event, store) => {
    setStoreMenuAnchor(event.currentTarget);
    setMenuStore(store);
  };

  const handleUserMenuOpen = (event, user) => {
    setUserMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const handleStoreMenuClose = () => {
    setStoreMenuAnchor(null);
    setMenuStore(null);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
    setMenuUser(null);
  };

  // Open dialogs
  const openEditStoreDialog = (store) => {
    setSelectedStore(store);
    setStoreForm({ 
      storeName: store.StoreName || '',
      storeCode: store.StoreCode || '',
      description: store.Description || '',
      street: store.Street || '',
      streetNumber: store.StreetNumber || '',
      postCode: store.PostCode || '',
      city: store.City || '',
      country: store.Country || '',
      email: store.Email || '',
      phoneNumber: store.PhoneNumber || '',
      ibaNumber: store.IBANumber || '',
      bicNumber: store.BICNumber || '',
      isActive: store.IsActive ?? true
    });
    setStoreDialogOpen(true);
    handleStoreMenuClose();
  };

  const openCreateStoreDialog = () => {
    setSelectedStore(null);
    setStoreForm({ 
      storeName: '',
      storeCode: '',
      description: '',
      street: '',
      streetNumber: '',
      postCode: '',
      city: '',
      country: '',
      email: '',
      phoneNumber: '',
      ibaNumber: '',
      bicNumber: '',
      isActive: true
    });
    setStoreDialogOpen(true);
  };

  const openCreateUserDialog = () => {
    setSelectedUser(null);
    setUserForm({ 
      userName: '',
      fullName: '', 
      email: '', 
      password: '', 
      confirmPassword: '',
      phoneNumber: '',
      roleName: 'User',
      storeId: null,
      notes: '',
      isActive: true 
    });
    setUserDialogOpen(true);
  };

  const openEditUserDialog = (user) => {
    setSelectedUser(user);
    setUserForm({ 
      userName: user.UserName || '',
      fullName: user.Name || user.FullName || '', 
      email: user.Email, 
      password: '', 
      confirmPassword: '',
      phoneNumber: user.PhoneNumber || '',
      roleName: user.Role || 'User',
      storeId: user.StoreId || null,
      notes: user.Notes || '',
      isActive: user.IsActive 
    });
    setUserDialogOpen(true);
    handleUserMenuClose();
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setPasswordForm({ password: '', confirmPassword: '' });
    setPasswordDialogOpen(true);
    handleUserMenuClose();
  };

  const openMoveUserDialog = (user) => {
    setSelectedUser(user);
    setTargetStoreId('');
    setMoveUserDialogOpen(true);
    handleUserMenuClose();
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.Role || '');
    setRoleDialogOpen(true);
    handleUserMenuClose();
  };

  const openDeleteDialog = (target, type) => {
    setDeleteTarget({ ...target, type });
    setDeleteDialogOpen(true);
    if (type === 'store') {
      handleStoreMenuClose();
    } else {
      handleUserMenuClose();
    }
  };

  // User card component
  const UserCard = ({ user, showStoreName = false }) => (
    <Card 
      sx={{ 
        mb: 1.5, 
        border: '1px solid',
        borderColor: user.IsActive ? 'divider' : 'error.light',
        backgroundColor: user.IsActive ? 'background.paper' : 'action.disabledBackground',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5} flex={1}>
            <Avatar 
              sx={{ 
                bgcolor: user.IsActive ? 'primary.main' : 'grey.500',
                width: 40,
                height: 40 
              }}
            >
              {user.Name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user.Name}
                </Typography>
                {!user.IsActive && (
                  <Chip 
                    label="Inactive" 
                    size="small" 
                    color="error" 
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {user.Email}
              </Typography>
              {showStoreName && user.StoreName && (
                <Chip 
                  label={user.StoreName} 
                  size="small" 
                  icon={<StoreIcon />}
                  sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
          
          <IconButton 
            size="small" 
            onClick={(e) => handleUserMenuOpen(e, user)}
            sx={{ ml: 1 }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Store & User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your stores and assign users efficiently
            </Typography>
          </Box>
          
          <Box display="flex" gap={1.5}>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={openCreateUserDialog}
              sx={{ borderRadius: 2 }}
            >
              Add User
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateStoreDialog}
              sx={{ borderRadius: 2 }}
            >
              Add Store
            </Button>
          </Box>
        </Box>

        {/* Search bar */}
        <TextField
          fullWidth
          placeholder="Search stores or users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': { 
              borderRadius: 2,
              backgroundColor: 'background.paper',
            }
          }}
        />
      </Box>

      {/* Statistics */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" fontWeight={700}>
                    {stores.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Stores
                  </Typography>
                </Box>
                <StoreIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" fontWeight={700}>
                    {Array.isArray(users) ? users.filter(u => u.IsActive).length : 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Users
                  </Typography>
                </Box>
                <GroupIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" fontWeight={700}>
                    {groupedUsers.unassigned?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Unassigned Users
                  </Typography>
                </Box>
                <PersonOffIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Unassigned Users Section */}
      {filteredUnassignedUsers.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, border: '2px dashed', borderColor: 'warning.main' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PersonOffIcon color="warning" />
              <Typography variant="h6" fontWeight={600}>
                Unassigned Users ({filteredUnassignedUsers.length})
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              These users are not assigned to any store. Assign them to a store to get started.
            </Alert>
            <Grid container spacing={2}>
              {filteredUnassignedUsers.map(user => (
                <Grid item xs={12} sm={6} md={4} key={user.Id}>
                  <UserCard user={user} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Stores Section Header */}
      {stores.length > 0 && (
        <Box mb={2}>
          <Typography variant="h5" fontWeight={600}>
            All Stores ({filteredStores.length})
          </Typography>
          {searchTerm && (
            <Typography variant="body2" color="text.secondary">
              Showing results for "{searchTerm}"
            </Typography>
          )}
        </Box>
      )}

      {/* Stores with Users */}
      {filteredStores.length > 0 && (
        <Grid container spacing={3}>
        {filteredStores.map(store => (
          <Grid item xs={12} md={6} key={store.Id}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                {/* Store Header */}
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                  mb={2}
                  pb={2}
                  borderBottom="2px solid"
                  borderColor="divider"
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      <StoreIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {store.StoreName}
                      </Typography>
                      {store.StoreCode && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          🏷️ {store.StoreCode}
                        </Typography>
                      )}
                      {store.City && (
                        <Typography variant="caption" color="text.secondary">
                          📍 {store.City}{store.Country && `, ${store.Country}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <Badge 
                      badgeContent={store.TotalUsers || groupedUsers[store.Id]?.length || 0} 
                      color="primary"
                      sx={{ mr: 1 }}
                    >
                      <GroupIcon color="action" />
                    </Badge>
                    <IconButton 
                      size="small"
                      onClick={(e) => handleStoreMenuOpen(e, store)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Store Description */}
                {store.Description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2, fontStyle: 'italic' }}
                  >
                    {store.Description}
                  </Typography>
                )}

                {/* Users in Store */}
                <Box>
                  {groupedUsers[store.Id]?.length > 0 ? (
                    groupedUsers[store.Id].map(user => (
                      <UserCard key={user.Id} user={user} />
                    ))
                  ) : (
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        py: 3,
                        color: 'text.secondary',
                        backgroundColor: 'action.hover',
                        borderRadius: 2,
                      }}
                    >
                      <PersonOffIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                      <Typography variant="body2">
                        No users assigned to this store
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      )}

      {/* No Results State */}
      {stores.length > 0 && filteredStores.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 6, borderRadius: 2 }}>
          <SearchIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Stores Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            No stores match your search "{searchTerm}"
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSearchTerm('')}
          >
            Clear Search
          </Button>
        </Card>
      )}

      {/* Empty State */}
      {stores.length === 0 && !loading && (
        <Card sx={{ textAlign: 'center', py: 6, borderRadius: 2 }}>
          <StoreIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Stores Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Get started by creating your first store
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateStoreDialog}
          >
            Create Store
          </Button>
        </Card>
      )}

      {/* Store Menu */}
      <Menu
        anchorEl={storeMenuAnchor}
        open={Boolean(storeMenuAnchor)}
        onClose={handleStoreMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => openEditStoreDialog(menuStore)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Store</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleToggleStoreStatus(menuStore)}>
          <ListItemIcon>
            {menuStore?.IsActive ? (
              <CancelIcon fontSize="small" color="warning" />
            ) : (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {menuStore?.IsActive ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => openDeleteDialog(menuStore, 'store')}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Store</ListItemText>
        </MenuItem>
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => openEditUserDialog(menuUser)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openRoleDialog(menuUser)}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Role</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openPasswordDialog(menuUser)}>
          <ListItemIcon>
            <VpnKeyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Password</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openMoveUserDialog(menuUser)}>
          <ListItemIcon>
            <SwapIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move to Store</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleToggleUserStatus(menuUser)}>
          <ListItemIcon>
            {menuUser?.IsActive ? (
              <CancelIcon fontSize="small" color="warning" />
            ) : (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {menuUser?.IsActive ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => openDeleteDialog(menuUser, 'user')}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Store Dialog */}
      <Dialog 
        open={storeDialogOpen} 
        onClose={() => setStoreDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <StoreIcon />
            {selectedStore ? 'Edit Store' : 'Create New Store'}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pb: 3 }}>
          {/* Basic Information */}
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 2, mb: 1.5 }}>
            📋 Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Store Name"
                value={storeForm.storeName}
                onChange={(e) => setStoreForm({ ...storeForm, storeName: e.target.value })}
                required
                inputProps={{ maxLength: 100 }}
                helperText={`${storeForm.storeName.length}/100 characters`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Store Code"
                value={storeForm.storeCode}
                onChange={(e) => setStoreForm({ ...storeForm, storeCode: e.target.value.toUpperCase() })}
                required
                inputProps={{ maxLength: 10, pattern: '[A-Z0-9]*' }}
                helperText="2-10 uppercase letters/numbers"
                placeholder="STORE01"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={storeForm.description}
                onChange={(e) => setStoreForm({ ...storeForm, description: e.target.value })}
                multiline
                rows={2}
                inputProps={{ maxLength: 500 }}
                helperText={`${storeForm.description.length}/500 characters`}
              />
            </Grid>
          </Grid>

          {/* Address Information */}
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 3, mb: 1.5 }}>
            📍 Address Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Street"
                value={storeForm.street}
                onChange={(e) => setStoreForm({ ...storeForm, street: e.target.value })}
                required
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Street Number (Optional)"
                value={storeForm.streetNumber}
                onChange={(e) => setStoreForm({ ...storeForm, streetNumber: e.target.value })}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Post Code"
                value={storeForm.postCode}
                onChange={(e) => setStoreForm({ ...storeForm, postCode: e.target.value })}
                required
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={storeForm.city}
                onChange={(e) => setStoreForm({ ...storeForm, city: e.target.value })}
                required
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country"
                value={storeForm.country}
                onChange={(e) => setStoreForm({ ...storeForm, country: e.target.value })}
                required
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
          </Grid>

          {/* Contact Information */}
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 3, mb: 1.5 }}>
            📞 Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={storeForm.email}
                onChange={(e) => setStoreForm({ ...storeForm, email: e.target.value })}
                required
                inputProps={{ maxLength: 255 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                value={storeForm.phoneNumber}
                onChange={(e) => setStoreForm({ ...storeForm, phoneNumber: e.target.value })}
                required
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          </Grid>

          {/* Banking Information */}
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 3, mb: 1.5 }}>
            🏦 Banking Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IBAN Number (Optional)"
                value={storeForm.ibaNumber}
                onChange={(e) => setStoreForm({ ...storeForm, ibaNumber: e.target.value })}
                placeholder="BE00 0000 0000 0000"
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="BIC Number (Optional)"
                value={storeForm.bicNumber}
                onChange={(e) => setStoreForm({ ...storeForm, bicNumber: e.target.value })}
                placeholder="BPOTBEB1"
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setStoreDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={selectedStore ? handleUpdateStore : handleCreateStore}
          >
            {selectedStore ? 'Update Store' : 'Create Store'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Dialog */}
      <Dialog 
        open={userDialogOpen} 
        onClose={() => setUserDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon />
            {selectedUser ? 'Edit User' : 'Create New User'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Basic Information */}
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 1, mb: 1.5 }}>
            👤 Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={userForm.userName}
                onChange={(e) => setUserForm({ ...userForm, userName: e.target.value })}
                required
                inputProps={{ minLength: 3, maxLength: 50 }}
                helperText={`${userForm.userName.length}/50 characters (min 3)`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={userForm.fullName}
                onChange={(e) => setUserForm({ ...userForm, fullName: e.target.value })}
                required
                inputProps={{ maxLength: 100 }}
                helperText={`${userForm.fullName.length}/100 characters`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                required
                inputProps={{ maxLength: 256 }}
                helperText={`${userForm.email.length}/256 characters`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                type="tel"
                value={userForm.phoneNumber}
                onChange={(e) => setUserForm({ ...userForm, phoneNumber: e.target.value })}
                inputProps={{ maxLength: 20 }}
                helperText={`${userForm.phoneNumber.length}/20 characters`}
              />
            </Grid>
          </Grid>

          {/* Security */}
          {!selectedUser && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 3, mb: 1.5 }}>
                🔒 Security
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    required
                    inputProps={{ minLength: 6, maxLength: 100 }}
                    helperText="6-100 characters"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={userForm.confirmPassword}
                    onChange={(e) => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                    required
                    helperText="Must match password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Role & Assignment */}
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 3, mb: 1.5 }}>
            🎭 Role & Store Assignment
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Role"
                value={userForm.roleName}
                onChange={(e) => setUserForm({ ...userForm, roleName: e.target.value })}
                required
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Assign to Store (Optional)"
                value={userForm.storeId || ''}
                onChange={(e) => setUserForm({ ...userForm, storeId: e.target.value || null })}
              >
                <MenuItem value="">No Store</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.Id} value={store.Id}>
                    {store.StoreName} {store.City && `(${store.City})`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Notes */}
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mt: 3, mb: 1.5 }}>
            📝 Additional Information
          </Typography>
          <TextField
            fullWidth
            label="Notes (Optional)"
            value={userForm.notes}
            onChange={(e) => setUserForm({ ...userForm, notes: e.target.value })}
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
            helperText={`${userForm.notes.length}/500 characters - For admin use`}
            placeholder="Any additional notes about this user..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUserDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={selectedUser ? handleUpdateUser : handleCreateUser}
          >
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VpnKeyIcon />
            Change Password
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Changing password for: <strong>{selectedUser?.Name}</strong>
          </Alert>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={passwordForm.password}
            onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
            margin="normal"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            margin="normal"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPasswordDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpdatePassword}>
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Change role for <strong>{selectedUser?.Name}</strong>
            </Typography>
            <TextField
              fullWidth
              select
              label="New Role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              margin="normal"
            >
              {roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleChangeRole} variant="contained" color="primary">
            Change Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move User Dialog */}
      <Dialog 
        open={moveUserDialogOpen} 
        onClose={() => setMoveUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SwapIcon />
            Move User to Store
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Moving user: <strong>{selectedUser?.Name}</strong>
          </Alert>
          <TextField
            fullWidth
            select
            label="Target Store"
            value={targetStoreId}
            onChange={(e) => setTargetStoreId(e.target.value)}
            margin="normal"
            required
          >
            <MenuItem value="unassign">Unassign from Store</MenuItem>
            <Divider />
            {stores.map(store => (
              <MenuItem key={store.Id} value={store.Id}>
                {store.StoreName} {store.City && `(${store.City})`}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMoveUserDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMoveUser}>
            Move User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} color="error.main">
            <DeleteIcon />
            Confirm Delete
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete this {deleteTarget?.type}?
            <br />
            <strong>{deleteTarget?.StoreName || deleteTarget?.Name}</strong>
            <br />
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={deleteTarget?.type === 'store' ? handleDeleteStore : handleDeleteUser}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddStoreAndUser;
